import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface SessionUser {
  id: string;
  email: string;
  displayName?: string;
  role?: string;
  /** Permission strings from the JWT, or "*" for super-admin. */
  perms?: string | string[];
  /** Tenants this user may act in (from /auth/me). */
  tenants?: string[];
}

interface SessionState {
  tenant: string;
  workspace: string | null;
  user: SessionUser | null;
  setTenant: (t: string) => void;
  setWorkspace: (w: string | null) => void;
  setUser: (u: SessionUser | null) => void;
}

/**
 * Tenant/workspace selection persists across reloads. `user` is hydrated from
 * `/api/auth/me` at boot (Phase 3) and is not persisted.
 */
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      tenant: "default",
      workspace: null,
      user: null,
      setTenant: (tenant) => set({ tenant, workspace: null }),
      setWorkspace: (workspace) => set({ workspace }),
      setUser: (user) => set({ user }),
    }),
    {
      name: "udip.session",
      partialize: (s) => ({ tenant: s.tenant, workspace: s.workspace }),
    },
  ),
);

export function hasPermission(user: SessionUser | null, perm: string): boolean {
  if (!user) return false;
  if (user.perms === "*") return true;
  const list = Array.isArray(user.perms)
    ? user.perms
    : (user.perms ?? "").split(",").map((p) => p.trim());
  return list.includes(perm);
}

export function isSystemAdmin(user: SessionUser | null): boolean {
  return user?.role === "SYSTEM_ADMIN" || user?.perms === "*";
}
